import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import "./App.css";
import { Button } from "./components/ui/button";
import { Card } from "./components/ui/card";
import { Badge } from "./components/ui/badge";
import { Input } from "./components/ui/input";
import {
  Satellite,
  Bot,
  AlertTriangle,
  BarChart3,
  X,
  Activity,
  Info,
  Github,
  Youtube,
} from "lucide-react";
import ThreeGlobe from "./components/ThreeGlobe";
import PredictionChart from "./components/PredictionChart";
import DecryptedText from "./components/DecryptedText";
import "./components/DecryptedText.css";
import videoBackground from "./assets/galaxy3.mp4";
import ReactMarkdown from "react-markdown";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ModelDiagnostics from "./components/ModelDiagnostics";

// Configure multiple backend URLs
const DATA_BACKEND_URL =
  process.env.REACT_APP_DATA_BACKEND_URL || "http://127.0.0.1:8000";
const AI_BACKEND_URL =
  process.env.REACT_APP_AI_BACKEND_URL || "http://127.0.0.1:8001";

const DATA_API = `${DATA_BACKEND_URL}/api`;
const AI_API = `${AI_BACKEND_URL}`;

// Backend Status Indicator Component
const BackendStatusIndicator = ({ backendStatus }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case "ready":
        return "#22c55e";
      case "error":
        return "#ef4444";
      case "checking":
        return "#eab308";
      default:
        return "#9ca3af";
    }
  };

  const getStatusIcon = () => {
    const { data, ai } = backendStatus;

    if (data.status === "ready" && ai.status === "ready") {
      return "🟢";
    }
    if (data.status === "checking" || ai.status === "checking") {
      return "🟡";
    }
    if (data.status === "error" && ai.status === "error") {
      return "🔴";
    }
    return "🟠";
  };

  const getStatusText = () => {
    const { data, ai } = backendStatus;

    if (data.status === "ready" && ai.status === "ready") {
      return "All Systems Online";
    }
    if (data.status === "checking" || ai.status === "checking") {
      const maxAttempts = Math.max(data.attempts, ai.attempts);
      return `Initializing Systems${
        maxAttempts > 1 ? ` (${maxAttempts}/3)` : "..."
      }`;
    }
    if (data.status === "error" && ai.status === "error") {
      return "Systems Offline";
    }
    if (data.status === "error") {
      return "Data System Offline";
    }
    if (ai.status === "error") {
      return "AI System Offline";
    }
    return "Connecting...";
  };

  //   const handleStatusClick = () => {
  //     const details = `
  // Mission Control Status:

  // 📊 Data Backend: ${backendStatus.data.status.toUpperCase()}
  //    Status: ${backendStatus.data.message}
  //    Attempts: ${backendStatus.data.attempts}
  //    URL: ${DATA_BACKEND_URL}

  // 🤖 AI Backend: ${backendStatus.ai.status.toUpperCase()}
  //    Status: ${backendStatus.ai.message}
  //    Attempts: ${backendStatus.ai.attempts}
  //    URL: ${AI_BACKEND_URL}
  //     `.trim();

  //     alert(details);
  //   };

  const handleStatusClick = () => {
    const { data, ai } = backendStatus;

    const getStatusEmoji = (status) => {
      if (status === "ready") return "✅";
      if (status === "checking") return "🔄";
      if (status === "error") return "❌";
      return "⚠️";
    };

    toast.info(
      <div style={{ fontSize: "13px", lineHeight: "1.6" }}>
        <div
          style={{ fontWeight: "bold", marginBottom: "12px", fontSize: "14px" }}
        >
          🛰️ Mission Control Status
        </div>

        <div style={{ marginBottom: "10px" }}>
          <div style={{ fontWeight: "600", marginBottom: "4px" }}>
            {getStatusEmoji(data.status)} Data Backend
          </div>
          <div style={{ paddingLeft: "20px", color: "#e0e0e0" }}>
            Status: {data.message}
            <br />
            Attempts: {data.attempts}
            <br />
            URL: {DATA_BACKEND_URL}
          </div>
        </div>

        <div>
          <div style={{ fontWeight: "600", marginBottom: "4px" }}>
            {getStatusEmoji(ai.status)} AI Backend
          </div>
          <div style={{ paddingLeft: "20px", color: "#e0e0e0" }}>
            Status: {ai.message}
            <br />
            Attempts: {ai.attempts}
            <br />
            URL: {AI_BACKEND_URL}
          </div>
        </div>
      </div>,
      {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "dark",
        style: {
          background: "rgba(0, 0, 0, 0.95)",
          border: "1px solid rgba(255, 255, 255, 0.2)",
          backdropFilter: "blur(10px)",
        },
      }
    );
  };

  return (
    <div
      onClick={handleStatusClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        fontSize: "12px",
        padding: "6px 12px",
        borderRadius: "12px",
        background: "rgba(0, 0, 0, 0.5)",
        border: "1px solid rgba(255, 255, 255, 0.1)",
        cursor: "pointer",
        transition: "all 0.2s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "rgba(0, 0, 0, 0.7)";
        e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.2)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "rgba(0, 0, 0, 0.5)";
        e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.1)";
      }}
      title="Click for detailed status"
    >
      <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
        <div
          style={{
            width: "8px",
            height: "8px",
            borderRadius: "50%",
            backgroundColor: getStatusColor(backendStatus.data.status),
            animation:
              backendStatus.data.status === "checking"
                ? "pulse 2s infinite"
                : "none",
            boxShadow:
              backendStatus.data.status === "ready"
                ? "0 0 8px rgba(34, 197, 94, 0.5)"
                : "none",
          }}
          title={`Data Backend: ${backendStatus.data.message}`}
        />

        <div
          style={{
            width: "8px",
            height: "8px",
            borderRadius: "50%",
            backgroundColor: getStatusColor(backendStatus.ai.status),
            animation:
              backendStatus.ai.status === "checking"
                ? "pulse 2s infinite"
                : "none",
            boxShadow:
              backendStatus.ai.status === "ready"
                ? "0 0 8px rgba(34, 197, 94, 0.5)"
                : "none",
          }}
          title={`AI Backend: ${backendStatus.ai.message}`}
        />
      </div>

      <span style={{ color: "#fff", fontWeight: "500", fontSize: "11px" }}>
        {getStatusIcon()} {getStatusText()}
      </span>
    </div>
  );
};

function App() {
  const [satellites, setSatellites] = useState([]);
  const [selectedSatellite, setSelectedSatellite] = useState(null);
  const [satelliteSummary, setSatelliteSummary] = useState(null);
  const [predictions, setPredictions] = useState([]);
  const [historicalData, setHistoricalData] = useState([]);
  const [showAIAgent, setShowAIAgent] = useState(false);
  const [agentQuery, setAgentQuery] = useState("");
  const [agentResponse, setAgentResponse] = useState(null);
  const [agentLoading, setAgentLoading] = useState(false);
  const [constellationFilter, setConstellationFilter] = useState("all");
  const [expandedChart, setExpandedChart] = useState(null);
  const [error, setError] = useState(null);
  const videoRef = useRef(null);
  const [backendStatus, setBackendStatus] = useState({
    data: { status: "checking", attempts: 0, message: "Initializing..." },
    ai: { status: "checking", attempts: 0, message: "Initializing..." },
  });
  const [showDiagnosticsModal, setShowDiagnosticsModal] = useState(false);
  const [showAboutModal, setShowAboutModal] = useState(false);
  const [isDraggingImage, setIsDraggingImage] = useState(false);

  // Helper function to sleep
  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  // Function to check a single backend with retry logic
  // const checkBackendHealth = useCallback(
  //   async (url, backendName, maxRetries = 3) => {
  //     for (let attempt = 1; attempt <= maxRetries; attempt++) {
  //       try {
  //         console.log(
  //           `🔍 Checking ${backendName} backend (attempt ${attempt}/${maxRetries})...`
  //         );
  //         console.log(`   URL: ${url}`);

  //         setBackendStatus((prev) => ({
  //           ...prev,
  //           [backendName]: {
  //             status: "checking",
  //             attempts: attempt,
  //             message: `Waking up... (${attempt}/${maxRetries})`,
  //           },
  //         }));

  //         const response = await axios.get(url, {
  //           timeout: 20000,
  //         });

  //         console.log(`✅ ${backendName} backend is ready:`, response.data);

  //         setBackendStatus((prev) => ({
  //           ...prev,
  //           [backendName]: {
  //             status: "ready",
  //             attempts: attempt,
  //             message: "Online",
  //           },
  //         }));

  //         return { success: true, data: response.data };
  //       } catch (error) {
  //         console.error(
  //           `❌ ${backendName} backend check failed (attempt ${attempt}):`,
  //           error.message
  //         );

  //         if (attempt < maxRetries) {
  //           const waitTime = Math.pow(2, attempt) * 4000;
  //           console.log(`⏳ Retrying ${backendName} in ${waitTime / 1000}s...`);

  //           setBackendStatus((prev) => ({
  //             ...prev,
  //             [backendName]: {
  //               status: "checking",
  //               attempts: attempt,
  //               message: `Retrying in ${waitTime / 1000}s...`,
  //             },
  //           }));

  //           await sleep(waitTime);
  //         } else {
  //           console.error(
  //             `💥 ${backendName} backend failed after ${maxRetries} attempts`
  //           );
  //           setBackendStatus((prev) => ({
  //             ...prev,
  //             [backendName]: {
  //               status: "error",
  //               attempts: attempt,
  //               message: "Offline",
  //             },
  //           }));
  //           return { success: false, error: error.message };
  //         }
  //       }
  //     }
  //   },
  //   []
  // );

  const checkBackendHealth = useCallback(
    async (url, backendName, maxRetries = 5) => {
      // <-- 1. Increased retries
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          console.log(
            `🔍 Checking ${backendName} backend (attempt ${attempt}/${maxRetries})...`
          );
          console.log(`   URL: ${url}`);

          setBackendStatus((prev) => ({
            ...prev,
            [backendName]: {
              status: "checking",
              attempts: attempt,
              message: `Waking up... (${attempt}/${maxRetries})`,
            },
          }));

          const response = await axios.get(url, {
            timeout: 40000, // <-- 2. Increased timeout to 60 seconds
          });

          // vvv 3. THIS IS THE KEY ADDITION vvv
          // Check if the backend (like your AI agent) responded but is still initializing
          if (response.data && response.data.status === "initializing") {
            // This is not a "ready" signal. We throw an error to intentionally
            // trigger our own catch block, which handles the retry logic.
            throw new Error(
              `${backendName} is still initializing (loading data)...`
            );
          }
          // ^^^ END OF ADDITION ^^^

          console.log(`✅ ${backendName} backend is ready:`, response.data);

          setBackendStatus((prev) => ({
            ...prev,
            [backendName]: {
              status: "ready",
              attempts: attempt,
              message: "Online",
            },
          }));

          return { success: true, data: response.data };
        } catch (error) {
          // This block now catches real errors (like timeouts) AND our "initializing" error
          console.error(
            `❌ ${backendName} backend check failed (attempt ${attempt}):`,
            error.message
          );

          if (attempt < maxRetries) {
            const waitTime = Math.pow(2, attempt) * 4000; // Keep exponential backoff
            console.log(`⏳ Retrying ${backendName} in ${waitTime / 1000}s...`);

            setBackendStatus((prev) => ({
              ...prev,
              [backendName]: {
                status: "checking",
                attempts: attempt,
                message: `Retrying in ${waitTime / 1000}s...`,
              },
            }));

            await sleep(waitTime);
          } else {
            console.error(
              `💥 ${backendName} backend failed after ${maxRetries} attempts`
            );
            setBackendStatus((prev) => ({
              ...prev,
              [backendName]: {
                status: "error",
                attempts: attempt,
                message: "Offline",
              },
            }));
            return { success: false, error: error.message };
          }
        }
      }
    },
    [] // Your original dependency array is fine
  );

  // Main function to wake up all backends
  const wakeUpBackends = useCallback(async () => {
    console.log("🚀 Starting Mission Control - Waking up backend services...");
    console.log(`   Data Backend: ${DATA_BACKEND_URL}`);
    console.log(`   AI Backend: ${AI_BACKEND_URL}`);

    const results = await Promise.allSettled([
      checkBackendHealth(`${DATA_API}/satellites`, "data", 3),
      checkBackendHealth(`${AI_API}/health`, "ai", 3),
    ]);

    const dataResult =
      results[0].status === "fulfilled" ? results[0].value : { success: false };
    const aiResult =
      results[1].status === "fulfilled" ? results[1].value : { success: false };

    if (dataResult.success && aiResult.success) {
      console.log("✅ ALL SYSTEMS OPERATIONAL - Mission Control Ready!");
    } else {
      console.warn("⚠️ PARTIAL SYSTEM FAILURE");
      if (!dataResult.success) {
        console.error("   ❌ Data Backend: OFFLINE");
        setError("Data backend is unavailable. Some features may not work.");
      }
      if (!aiResult.success) {
        console.error("   ❌ AI Backend: OFFLINE");
      }
    }

    return {
      data: dataResult.success,
      ai: aiResult.success,
      allReady: dataResult.success && aiResult.success,
    };
  }, [checkBackendHealth]);

  useEffect(() => {
    wakeUpBackends();
    fetchSatellites();
  }, [wakeUpBackends]);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = 0.4;
    }
  }, []);

  const fetchSatellites = async () => {
    try {
      setError(null);
      const response = await axios.get(`${DATA_API}/satellites`);
      setSatellites(response.data);
    } catch (error) {
      console.error("Error fetching satellites:", error);
      setError("Could not connect to the mission control server.");
    }
  };

  const fetchSatelliteSummary = async (satelliteId) => {
    try {
      setError(null);
      const response = await axios.get(
        `${DATA_API}/satellite/${satelliteId}/summary`
      );
      setSatelliteSummary(response.data);
      setHistoricalData(response.data.historical || []);
      setPredictions(response.data.predictions || []);
    } catch (error) {
      console.error("Error fetching satellite data:", error);
      setError(`Failed to fetch data for satellite ${satelliteId}.`);
      setSatelliteSummary(null);
      setHistoricalData([]);
      setPredictions([]);
    }
  };

  const handleAnalyze = async () => {
    if (!selectedSatellite) return;

    setShowAIAgent(true);

    const analysisQuery = `Give me a comprehensive performance analysis of satellite ${selectedSatellite.satellite_id} (${selectedSatellite.constellation} constellation). Include:
1. Clock error analysis (trends, average, peak values)
2. Orbit error analysis (3D position accuracy)
3. Overall performance rating
4. Any anomalies or concerns
5. Comparison to typical ${selectedSatellite.constellation} performance standards`;

    setAgentQuery(analysisQuery);

    setTimeout(async () => {
      if (!analysisQuery.trim()) return;
      setAgentLoading(true);
      setAgentResponse(null);

      try {
        const response = await axios.post(`${AI_API}/chat`, {
          query: analysisQuery,
          format_type: "text",
        });

        if (response.data.success) {
          setAgentResponse({
            text_summary: response.data.text,
            timestamp: new Date().toISOString(),
            satellite_data: response.data.data,
            summary: response.data.summary,
            response_type: response.data.response_type,
          });
        } else {
          setAgentResponse({
            error:
              response.data.text || "Failed to get response from AI Agent.",
          });
        }
        setAgentLoading(false);
      } catch (error) {
        console.error("Error submitting AI query:", error);
        setAgentResponse({
          error:
            error.response?.data?.detail ||
            error.response?.data?.text ||
            "Failed to submit query to AI Agent.",
        });
        setAgentLoading(false);
      }
    }, 300);
  };

  const handleSatelliteClick = (satellite) => {
    setSelectedSatellite(satellite);
    fetchSatelliteSummary(satellite.satellite_id);
  };

  const handleCloseRightPanel = () => {
    setSelectedSatellite(null);
    setSatelliteSummary(null);
    setHistoricalData([]);
    setPredictions([]);
  };

  const handleChartExpand = (chartData, chartTitle, yLabel) => {
    setExpandedChart({
      data: chartData,
      title: chartTitle,
      yLabel: yLabel,
    });
  };

  const handleChartClose = () => {
    setExpandedChart(null);
  };

  const handleAIQuery = async () => {
    if (!agentQuery.trim()) return;
    setAgentLoading(true);
    setAgentResponse(null);

    try {
      const response = await axios.post(`${AI_API}/chat`, {
        query: agentQuery,
        format_type: "text",
      });

      if (response.data.success) {
        setAgentResponse({
          text_summary: response.data.text,
          timestamp: new Date().toISOString(),
          satellite_data: response.data.data,
          summary: response.data.summary,
          response_type: response.data.response_type,
        });
      } else {
        setAgentResponse({
          error: response.data.text || "Failed to get response from AI Agent.",
        });
      }
      setAgentLoading(false);
    } catch (error) {
      console.error("Error submitting AI query:", error);
      setAgentResponse({
        error:
          error.response?.data?.detail ||
          error.response?.data?.text ||
          "Failed to submit query to AI Agent.",
      });
      setAgentLoading(false);
    }
  };

  const getConstellationColorHex = (constellation) => {
    const colors = {
      GPS: "#22c55e",
      GLONASS: "#ef4444",
      Galileo: "#3b82f6",
      BeiDou: "#eab308",
      GEO: "#a855f7",
      MEO: "#06b6d4",
      LEO: "#f97316",
    };
    return colors[constellation] || "#9ca3af";
  };

  const filteredSatellites =
    constellationFilter === "all"
      ? satellites
      : satellites.filter((sat) => sat.constellation === constellationFilter);

  const renderPredictionCharts = () => {
    if (!predictions.length) return null;

    const clockData = {
      historical: historicalData.map((h) => ({
        timestamp: h.timestamp,
        value: h.pred_clock_error_m,
      })),
      predicted: predictions.map((p) => ({
        timestamp: p.timestamp,
        value: p.pred_clock_error_m,
      })),
    };

    const orbitData = {
      historical: historicalData.map((h) => ({
        timestamp: h.timestamp,
        value: h.pred_orbit_error_m,
      })),
      predicted: predictions.map((p) => ({
        timestamp: p.timestamp,
        value: p.pred_orbit_error_m,
      })),
    };

    return (
      <div className="charts-container">
        <div
          className="chart-wrapper"
          onClick={() =>
            handleChartExpand(
              clockData,
              "Clock Error Prediction",
              "Error (meters)"
            )
          }
        >
          <PredictionChart
            historicalData={clockData.historical}
            predictedData={clockData.predicted}
            title="Clock Error Prediction"
            yLabel="Error (meters)"
            isExpandable={true}
          />
        </div>
        <div
          className="chart-wrapper"
          onClick={() =>
            handleChartExpand(
              orbitData,
              "Orbit Error Prediction",
              "Error (meters)"
            )
          }
        >
          <PredictionChart
            historicalData={orbitData.historical}
            predictedData={orbitData.predicted}
            title="Orbit Error Prediction"
            yLabel="Error (meters)"
            isExpandable={true}
          />
        </div>

        {/* Model Diagnostics Card - clickable to open modal */}
        <div
          onClick={() => setShowDiagnosticsModal(true)}
          style={{ cursor: "pointer" }}
        >
          <ModelDiagnostics
            satelliteId={selectedSatellite.satellite_id}
            constellation={selectedSatellite.constellation}
            selectedSatellite={selectedSatellite}
            showTitle={true}
            onExpandClick={() => setShowDiagnosticsModal(true)}
          />
        </div>
      </div>
    );
  };

  return (
    <div
      className="App"
      style={{ position: "relative", width: "100vw", height: "100vh" }}
    >
      <video
        ref={videoRef}
        autoPlay
        loop
        muted
        playsInline
        className="background-video"
        src={videoBackground}
      />

      <header className="header" style={{ position: "relative", zIndex: 10 }}>
        <div className="header-content">
          <div className="logo">
            <div style={{ display: "flex", alignItems: "center" }}>
              <Satellite className="logo-icon" />
              <DecryptedText
                text=" TimeForge | GNSS "
                animateOn="view"
                revealDirection="center"
                maxIterations={25}
                encryptedClassName="scramble-text-encrypted"
                className="scramble-text-font"
              />
            </div>
          </div>
          <div className="header-controls">
            <BackendStatusIndicator backendStatus={backendStatus} />
            <select
              value={constellationFilter}
              onChange={(e) => setConstellationFilter(e.target.value)}
              className="constellation-filter"
            >
              <option value="all">All Satellites</option>
              <option value="GEO">GEO (Geostationary)</option>
              <option value="MEO">MEO (Medium Earth Orbit)</option>
              <option value="LEO">LEO (Low Earth Orbit)</option>
              <option value="GPS">GPS</option>
              <option value="GLONASS">GLONASS</option>
              <option value="Galileo">Galileo</option>
              <option value="BeiDou">BeiDou</option>
            </select>

            {/* About Button */}
            <Button
              onClick={() => setShowAboutModal(true)}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "36px",
                height: "36px",
                padding: "0",
                borderRadius: "8px",
                background: "rgba(30, 41, 59, 0.8)",
                border: "1px solid rgba(226, 232, 240, 0.2)",
                color: "#e2e8f0",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(30, 41, 59, 1)";
                e.currentTarget.style.borderColor = "rgba(96, 165, 250, 0.5)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(30, 41, 59, 0.8)";
                e.currentTarget.style.borderColor = "rgba(226, 232, 240, 0.2)";
              }}
              title="About Project"
            >
              <Info size={18} />
            </Button>

            <Button onClick={() => setShowAIAgent(true)} className="ai-button">
              <Bot size={16} />
              AI Agent
            </Button>
          </div>
        </div>
      </header>

      <div className="main-content" style={{ position: "relative", zIndex: 5 }}>
        <div
          className="satellite-list"
          style={{
            background: "rgba(0, 0, 0, 0.8)",
            backdropFilter: "blur(20px)",
            borderRadius: "12px",
            padding: "16px",
            maxHeight: "70vh",
            overflowY: "auto",
            border: "1px solid rgba(255, 255, 255, 0.1)",
          }}
        >
          <h3>Active Satellites ({filteredSatellites.length})</h3>
          {error && (
            <div className="error-message-inline">
              <AlertTriangle size={14} /> {error}
            </div>
          )}
          <div className="satellite-list-items">
            {filteredSatellites.map((satellite) => (
              <div
                key={satellite.satellite_id}
                className={`satellite-item ${
                  selectedSatellite?.satellite_id === satellite.satellite_id
                    ? "selected"
                    : ""
                }`}
                onClick={() => handleSatelliteClick(satellite)}
              >
                <div
                  className="constellation-dot"
                  style={{
                    backgroundColor: getConstellationColorHex(
                      satellite.constellation
                    ),
                  }}
                />
                <span className="satellite-id">{satellite.satellite_id}</span>
                <Badge
                  variant="outline"
                  className="constellation-badge"
                  style={{ color: "white" }}
                >
                  {satellite.constellation}
                </Badge>
                <Activity size={12} className="status-icon" />
              </div>
            ))}
          </div>
        </div>

        <div className="globe-container">
          <ThreeGlobe satellites={filteredSatellites} />
        </div>

        {selectedSatellite && (
          <div
            className="right-panel scrollable"
            style={{ position: "relative", zIndex: 10 }}
          >
            <Button
              onClick={handleCloseRightPanel}
              className="close-panel-button"
              variant="ghost"
              size="sm"
              style={{
                position: "absolute",
                top: "16px",
                right: "16px",
                zIndex: 11,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                borderRadius: "50%",
                width: "32px",
                height: "32px",
                padding: "0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <X size={16} />
            </Button>

            <div className="right-panel-content">
              <Card className="satellite-card">
                <div className="satellite-header">
                  <h2>{selectedSatellite.satellite_id}</h2>
                  <Badge
                    variant="secondary"
                    style={{
                      backgroundColor: `${getConstellationColorHex(
                        selectedSatellite.constellation
                      )}20`,
                      color: getConstellationColorHex(
                        selectedSatellite.constellation
                      ),
                    }}
                  >
                    {selectedSatellite.constellation}
                  </Badge>
                </div>

                {satelliteSummary && (
                  <div className="satellite-stats">
                    <div className="stat-item">
                      <span className="stat-label">Avg Clock Error</span>
                      <span className="stat-value">
                        {satelliteSummary.summary?.avg_clock_error?.toFixed(
                          5
                        ) || "0.0000"}{" "}
                        m
                      </span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Peak Orbit Error</span>
                      <span className="stat-value">
                        {satelliteSummary.summary?.peak_orbit_error?.toFixed(
                          2
                        ) || "0.00"}{" "}
                        m
                      </span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Data Points</span>
                      <span className="stat-value">
                        {satelliteSummary.summary?.data_points || 0}
                      </span>
                    </div>
                  </div>
                )}

                <div className="action-buttons">
                  <Button
                    onClick={() => setShowAIAgent(true)}
                    variant="outline"
                  >
                    <Bot size={16} />
                    Ask AI Agent
                  </Button>
                  <Button variant="outline" onClick={handleAnalyze}>
                    <BarChart3 size={16} />
                    Analyze
                  </Button>
                </div>
              </Card>

              {renderPredictionCharts()}
            </div>
          </div>
        )}
      </div>

      {showAIAgent && (
        <div
          className="ai-dialog-overlay"
          onClick={() => setShowAIAgent(false)}
          style={{ zIndex: 1000 }}
        >
          <div className="ai-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="ai-dialog-header">
              <h2>
                <Bot size={20} /> AI Agent Analysis
              </h2>
              <button
                className="close-button"
                onClick={() => setShowAIAgent(false)}
              >
                ×
              </button>
            </div>
            <div className="ai-examples">
              <h4>Example Queries:</h4>
              <div className="example-buttons">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    setAgentQuery("Compare GEO vs MEO2 orbit error")
                  }
                >
                  Compare satellite errors
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    setAgentQuery("Find satellites with peak clock error < 2m")
                  }
                >
                  Find low clock errors
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    setAgentQuery("Give MEO satellite performance analysis")
                  }
                >
                  MEO performance analysis
                </Button>
              </div>
            </div>
            <div className="ai-input">
              <Input
                placeholder="Ask me about satellite performance, errors, comparisons..."
                value={agentQuery}
                onChange={(e) => setAgentQuery(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleAIQuery()}
              />
              <Button onClick={handleAIQuery} disabled={agentLoading}>
                {agentLoading ? "Analyzing..." : "Analyze"}
              </Button>
            </div>
            {agentResponse && (
              <div className="ai-response">
                <h4>Analysis Results:</h4>
                {agentResponse.error ? (
                  <div className="error-message">
                    <AlertTriangle size={16} />
                    {agentResponse.error}
                  </div>
                ) : (
                  <div>
                    <div className="response-text">
                      <ReactMarkdown>
                        {String(agentResponse.text_summary)}
                      </ReactMarkdown>
                    </div>

                    {agentResponse.satellite_data &&
                      Object.keys(agentResponse.satellite_data).length > 0 && (
                        <div className="response-stats">
                          <h5>Satellite Data:</h5>
                          <pre>
                            {JSON.stringify(
                              agentResponse.satellite_data,
                              null,
                              2
                            )}
                          </pre>
                        </div>
                      )}
                    {agentResponse.summary && (
                      <div className="response-summary">
                        <h5>Summary:</h5>
                        <p>{agentResponse.summary}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {expandedChart && (
        <div
          className="chart-modal-overlay"
          onClick={handleChartClose}
          style={{ zIndex: 1000 }}
        >
          <div className="chart-modal" onClick={(e) => e.stopPropagation()}>
            <div className="chart-modal-header">
              <h2>
                {expandedChart.title} - {selectedSatellite?.satellite_id}
              </h2>
              <button className="close-button" onClick={handleChartClose}>
                ×
              </button>
            </div>
            <div className="chart-modal-content">
              <PredictionChart
                historicalData={expandedChart.data.historical}
                predictedData={expandedChart.data.predicted}
                title={expandedChart.title}
                yLabel={expandedChart.yLabel}
                isExpanded={true}
              />
            </div>
          </div>
        </div>
      )}
      {/* Diagnostics Modal - Center Screen
      // {showDiagnostics && selectedSatellite && (
      //   <div
      //     className="chart-modal-overlay"
      //     onClick={() => setShowDiagnostics(false)}
      //     style={{ zIndex: 1000 }}
      //   >
      //     <div
      //       className="chart-modal"
      //       onClick={(e) => e.stopPropagation()}
      //       style={{ maxWidth: "1400px", width: "95vw" }}
      //     >
      //       <div className="chart-modal-header">
      //         <h2>Model Diagnostics - {selectedSatellite.satellite_id}</h2>
      //         <button
      //           className="close-button"
      //           onClick={() => setShowDiagnostics(false)}
      //         >
      //           ×
      //         </button>
      //       </div>
      //       <div className="chart-modal-content">
      //         <ModelDiagnostics
      //           satelliteId={selectedSatellite.satellite_id}
      //           constellation={selectedSatellite.constellation}
      //           selectedSatellite={selectedSatellite}
      //           showTitle={false}
      //         />
      //       </div>
      //     </div>
      //   </div>
      // )} */}

      {/* Diagnostics Full Screen Modal */}
      {/* {showDiagnosticsModal && selectedSatellite && (
        <div
          className="chart-modal-overlay"
          onClick={(e) => {
            // Only close if clicking directly on the overlay, not when dragging
            if (e.target === e.currentTarget) {
              setShowDiagnosticsModal(false);
            }
          }}
          style={{ zIndex: 1000 }}
        >
          <div
            className="chart-modal"
            onClick={(e) => e.stopPropagation()}
            onMouseLeave={(e) => {
              // Prevent closing when mouse leaves while dragging
              e.stopPropagation();
            }}
            style={{
              maxWidth: "1400px",
              width: "95vw",
              maxHeight: "85vh",
            }}
          >
            <div
              className="chart-modal-header"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "1.5rem 2rem",
                borderBottom: "1px solid rgba(226, 232, 240, 0.1)",
                background: "rgba(30, 41, 59, 0.5)",
                flexShrink: 0,
              }}
            >
              <h2
                style={{
                  fontSize: "1.5rem",
                  fontWeight: "700",
                  color: "#f1f5f9",
                  margin: 0,
                }}
              >
                Model Diagnostics - {selectedSatellite.satellite_id}
              </h2>
              <button
                className="close-button"
                onClick={() => setShowDiagnosticsModal(false)}
                style={{
                  background: "rgba(239, 68, 68, 0.2)",
                  border: "1px solid rgba(239, 68, 68, 0.3)",
                  color: "#fca5a5",
                  fontSize: "1.5rem",
                  width: "40px",
                  height: "40px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.2s ease",
                  padding: 0,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(239, 68, 68, 0.3)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(239, 68, 68, 0.2)";
                }}
              >
                ×
              </button>
            </div>
            <div
              className="chart-modal-content"
              style={{
                flex: 1,
                padding: "2rem",
                overflowY: "auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "350px",
              }}
            >
              <ModelDiagnostics
                satelliteId={selectedSatellite.satellite_id}
                constellation={selectedSatellite.constellation}
                selectedSatellite={selectedSatellite}
                showTitle={false}
                isInModal={true}
              />
            </div>
          </div>
        </div>
      )}
       */}

      {/* Diagnostics Full Screen Modal */}
      {showDiagnosticsModal && selectedSatellite && (
        <div
          className="chart-modal-overlay"
          onMouseDown={(e) => {
            // Only allow closing if clicking directly on the overlay background
            if (e.target === e.currentTarget) {
              setShowDiagnosticsModal(false);
            }
          }}
          onMouseUp={() => {
            // Reset dragging state when mouse is released anywhere
            setIsDraggingImage(false);
          }}
          style={{
            zIndex: 1000,
          }}
        >
          <div
            className="chart-modal"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: "1400px",
              width: "95vw",
              maxHeight: "85vh",
            }}
          >
            <div
              className="chart-modal-header"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "1.5rem 2rem",
                borderBottom: "1px solid rgba(226, 232, 240, 0.1)",
                background: "rgba(30, 41, 59, 0.5)",
                flexShrink: 0,
              }}
            >
              <h2
                style={{
                  fontSize: "1.5rem",
                  fontWeight: "700",
                  color: "#f1f5f9",
                  margin: 0,
                }}
              >
                Model Diagnostics - {selectedSatellite.satellite_id}
              </h2>
              <button
                className="close-button"
                onClick={() => setShowDiagnosticsModal(false)}
                style={{
                  background: "rgba(239, 68, 68, 0.2)",
                  border: "1px solid rgba(239, 68, 68, 0.3)",
                  color: "#fca5a5",
                  fontSize: "1.5rem",
                  width: "40px",
                  height: "40px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.2s ease",
                  padding: 0,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(239, 68, 68, 0.3)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(239, 68, 68, 0.2)";
                }}
              >
                ×
              </button>
            </div>
            <div
              className="chart-modal-content"
              style={{
                flex: 1,
                padding: "2rem",
                // overflowY: "auto",
                overflowY: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "350px",
              }}
            >
              <ModelDiagnostics
                satelliteId={selectedSatellite.satellite_id}
                constellation={selectedSatellite.constellation}
                selectedSatellite={selectedSatellite}
                showTitle={false}
                isInModal={true}
                onDragStateChange={setIsDraggingImage}
              />
            </div>
          </div>
        </div>
      )}

      {/* About/Resources Modal */}
      {showAboutModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.95)",
            backdropFilter: "blur(10px)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
          onClick={() => setShowAboutModal(false)}
        >
          <div
            style={{
              background: "rgba(15, 20, 25, 0.95)",
              border: "1px solid rgba(226, 232, 240, 0.2)",
              borderRadius: "16px",
              padding: "32px",
              maxWidth: "600px",
              width: "100%",
              maxHeight: "85vh",
              overflowY: "auto",
              backdropFilter: "blur(20px)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "24px",
              }}
            >
              <h2
                style={{
                  fontSize: "24px",
                  fontWeight: "700",
                  color: "#f1f5f9",
                  margin: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <Satellite size={24} />
                About TimeForge
              </h2>
              <button
                onClick={() => setShowAboutModal(false)}
                style={{
                  background: "rgba(239, 68, 68, 0.2)",
                  border: "1px solid rgba(239, 68, 68, 0.3)",
                  color: "#fca5a5",
                  fontSize: "24px",
                  width: "40px",
                  height: "40px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.2s ease",
                  padding: 0,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(239, 68, 68, 0.3)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(239, 68, 68, 0.2)";
                }}
              >
                ×
              </button>
            </div>

            {/* Project Description */}
            <div style={{ marginBottom: "28px" }}>
              <p
                style={{
                  fontSize: "14px",
                  color: "#cbd5e1",
                  lineHeight: "1.6",
                  margin: 0,
                }}
              >
                TimeForge is an AI/ML platform developed for the Smart India
                Hackathon 2025 to predict satellite clock and orbit errors.
                Using 7 days of historical data, it forecasts errors for the
                next 24 hours by integrating novel astro-physical features with
                a hybrid multi-model ensemble. The platform also features an
                'Agentic AI' that acts as a virtual data scientist, allowing
                users to ask natural language questions about satellite
                performance.
              </p>
            </div>

            {/* Links Section */}
            <div style={{ display: "grid", gap: "16px", marginBottom: "28px" }}>
              {/* GitHub Link */}
              <a
                href="https://github.com/Mapicx/TimeForge_2.0.git"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "16px",
                  background: "rgba(30, 41, 59, 0.6)",
                  border: "1px solid rgba(226, 232, 240, 0.15)",
                  borderRadius: "10px",
                  color: "#e2e8f0",
                  textDecoration: "none",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(30, 41, 59, 1)";
                  e.currentTarget.style.borderColor = "rgba(96, 165, 250, 0.5)";
                  e.currentTarget.style.transform = "translateX(4px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(30, 41, 59, 0.6)";
                  e.currentTarget.style.borderColor =
                    "rgba(226, 232, 240, 0.15)";
                  e.currentTarget.style.transform = "translateX(0)";
                }}
              >
                <Github size={20} style={{ color: "#60a5fa", flexShrink: 0 }} />
                <div>
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#f1f5f9",
                    }}
                  >
                    GitHub Repository
                  </div>
                  <div style={{ fontSize: "12px", color: "#94a3b8" }}>
                    View source code
                  </div>
                </div>
              </a>

              {/* YouTube Link */}
              <a
                href="https://youtu.be/N4QNHHZ4XA8?si=ee0kPGxBlSwIJ3rJ"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "16px",
                  background: "rgba(30, 41, 59, 0.6)",
                  border: "1px solid rgba(226, 232, 240, 0.15)",
                  borderRadius: "10px",
                  color: "#e2e8f0",
                  textDecoration: "none",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(30, 41, 59, 1)";
                  e.currentTarget.style.borderColor = "rgba(239, 68, 68, 0.5)";
                  e.currentTarget.style.transform = "translateX(4px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(30, 41, 59, 0.6)";
                  e.currentTarget.style.borderColor =
                    "rgba(226, 232, 240, 0.15)";
                  e.currentTarget.style.transform = "translateX(0)";
                }}
              >
                <Youtube
                  size={20}
                  style={{ color: "#f97316", flexShrink: 0 }}
                />
                <div>
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#f1f5f9",
                    }}
                  >
                    Demo Video
                  </div>
                  <div style={{ fontSize: "12px", color: "#94a3b8" }}>
                    Watch project walkthrough
                  </div>
                </div>
              </a>
            </div>

            {/* Team Section */}
            <div
              style={{
                padding: "16px",
                background: "rgba(34, 197, 94, 0.05)",
                border: "1px solid rgba(34, 197, 94, 0.2)",
                borderRadius: "10px",
                marginBottom: "12px",
              }}
            >
              <div
                style={{
                  fontSize: "12px",
                  color: "#94a3b8",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  marginBottom: "8px",
                  fontWeight: "600",
                }}
              >
                Team
              </div>
              <div
                style={{
                  fontSize: "14px",
                  color: "#e2e8f0",
                  lineHeight: "1.6",
                }}
              >
                Developed by Team TimeForge for the Smart India Hackathon 2025.
                {/* <br /> */}
              </div>
            </div>

            {/* Tech Stack */}
            <div
              style={{
                padding: "16px",
                background: "rgba(59, 130, 246, 0.05)",
                border: "1px solid rgba(59, 130, 246, 0.2)",
                borderRadius: "10px",
              }}
            >
              <div
                style={{
                  fontSize: "12px",
                  color: "#94a3b8",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  marginBottom: "8px",
                  fontWeight: "600",
                }}
              >
                Tech Stack
              </div>
              <div
                style={{
                  fontSize: "14px",
                  color: "#e2e8f0",
                  lineHeight: "1.6",
                }}
              >
                React • Python • FastAPI • PyTorch • Supabase • Scikit-learn •
                LangChain
                <br />
                Three.js • React-Plotly • Tailwind CSS • Astropy • Docker • AWS
              </div>
            </div>
          </div>
        </div>
      )}

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
      />
    </div>
  );
}

export default App;
